import { useEffect, useState } from "react";
import {
  Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning,
  Droplets, Wind, Thermometer, Sunrise, Sunset, Umbrella,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";

export const WEATHER_ICONS = {
  sun: Sun,
  cloud: Cloud,
  "cloud-sun": CloudSun,
  "cloud-fog": CloudFog,
  "cloud-drizzle": CloudDrizzle,
  "cloud-rain": CloudRain,
  "cloud-snow": CloudSnow,
  "cloud-lightning": CloudLightning,
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl bg-white/10 p-3 flex flex-col gap-1 min-w-0" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="font-display text-xl font-black text-white leading-none">{value}</div>
  </div>
);

const HourCard = ({ h }) => {
  const Icon = WEATHER_ICONS[h.icon] || Cloud;
  const wet = h.rain_chance >= 50;
  return (
    <div
      className="shrink-0 snap-start w-[72px] rounded-2xl bg-white border border-gray-100 p-2.5 flex flex-col items-center gap-1.5 shadow-[0_4px_14px_rgba(13,27,75,0.06)]"
      data-testid={`weather-hour-${h.hour}`}
    >
      <div className="text-[11px] font-bold text-gray-500">{h.hour}</div>
      <Icon className={`w-5 h-5 ${wet ? "text-[var(--fi-blue)]" : "text-amber-500"}`} />
      <div className="font-display text-lg font-black text-[var(--fi-blue)] leading-none">{h.temp}°</div>
      <div className="w-full">
        <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full rounded-full bg-sky-500 transition-[width] duration-500" style={{ width: `${h.rain_chance}%` }} />
        </div>
        <div className={`mt-1 text-[10px] font-bold text-center ${wet ? "text-sky-600" : "text-gray-400"}`}>
          {h.rain_chance}%
        </div>
      </div>
    </div>
  );
};

const DayRow = ({ d, min, max }) => {
  const Icon = WEATHER_ICONS[d.icon] || Cloud;
  const span = Math.max(max - min, 1);
  const left = ((d.temp_min - min) / span) * 100;
  const width = ((d.temp_max - d.temp_min) / span) * 100;
  return (
    <div
      className="grid grid-cols-[52px_28px_44px_1fr] sm:grid-cols-[64px_32px_52px_1fr_120px] items-center gap-3 py-2.5 border-b border-gray-100 last:border-0"
      data-testid={`weather-day-${d.date}`}
    >
      <div>
        <div className="font-bold text-sm text-[var(--fi-blue)]">{d.weekday}</div>
        <div className="text-[10px] text-gray-400">{d.day_label}</div>
      </div>
      <Icon className="w-5 h-5 text-[var(--fi-blue)]" title={d.label} />
      <div className={`flex items-center gap-0.5 text-[11px] font-bold ${d.rain_chance >= 50 ? "text-sky-600" : "text-gray-400"}`}>
        <Umbrella className="w-3 h-3" /> {d.rain_chance}%
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-[var(--fi-blue)]/60 w-7 text-right">{d.temp_min}°</span>
        <div className="relative h-2 flex-1 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="absolute h-full rounded-full"
            style={{ left: `${left}%`, width: `${Math.max(width, 6)}%`, background: "linear-gradient(90deg,#38bdf8,#f59e0b,#d72638)" }}
          />
        </div>
        <span className="text-xs font-bold text-[var(--fi-red)] w-7">{d.temp_max}°</span>
      </div>
      <div className="hidden sm:block text-[11px] text-gray-500 truncate">
        Sensação {d.feels_min}° / {d.feels_max}°
      </div>
    </div>
  );
};

const WeatherDetailSheet = ({ cityId, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!cityId) return;
    setData(null);
    setError(false);
    let cancelled = false;
    api
      .get(`/weather/cities/${cityId}`)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [cityId]);

  const cur = data?.current;
  const Icon = WEATHER_ICONS[cur?.icon] || Cloud;
  const allMin = data ? Math.min(...data.daily.map((d) => d.temp_min)) : 0;
  const allMax = data ? Math.max(...data.daily.map((d) => d.temp_max)) : 0;
  const today = data?.daily?.[0];

  return (
    <Sheet open={!!cityId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="bottom"
        data-testid="weather-detail-sheet"
        className="p-0 border-0 rounded-t-3xl max-h-[90vh] overflow-y-auto bg-[var(--fi-bg)] sm:max-w-3xl sm:mx-auto [&>button]:text-white [&>button]:opacity-80"
      >
        <div className="relative overflow-hidden px-5 pt-6 pb-5" style={{ background: "linear-gradient(135deg,#0D1B4B 0%,#1a2960 60%,#0D1B4B 100%)" }}>
          <div className="absolute -top-20 -right-16 w-56 h-56 rounded-full opacity-30 blur-3xl fi-gradient" />
          <div className="mx-auto w-10 h-1 rounded-full bg-white/30 mb-4 sm:hidden" />
          <SheetHeader className="text-left space-y-0 relative">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">Previsão completa</div>
            <SheetTitle className="font-display text-3xl sm:text-4xl font-black uppercase text-white leading-none" data-testid="weather-detail-city">
              {data?.name || "Carregando…"}
            </SheetTitle>
            <SheetDescription className="text-white/60 text-sm">
              {cur ? `${cur.label} · atualizado ${cur.time?.slice(11, 16) || ""}` : "Buscando dados da Open-Meteo"}
            </SheetDescription>
          </SheetHeader>

          {error && (
            <div className="mt-4 text-white/80 text-sm" data-testid="weather-detail-error">
              Previsão indisponível no momento. Tente novamente em instantes.
            </div>
          )}

          {!data && !error && <div className="mt-5 h-24 rounded-2xl bg-white/10 animate-pulse" />}

          {cur && (
            <div className="relative mt-4 flex items-end gap-4">
              <div className="font-display text-7xl font-black text-white leading-none" data-testid="weather-detail-temp">{cur.temp}°</div>
              <div className="pb-1">
                <Icon className="w-12 h-12 text-white/90" />
                {today && (
                  <div className="text-xs text-white/70 font-bold mt-1">
                    ↓ {today.temp_min}° ↑ {today.temp_max}°
                  </div>
                )}
              </div>
            </div>
          )}

          {cur && (
            <div className="relative mt-4 grid grid-cols-3 gap-2">
              <Stat icon={Thermometer} label="Sensação" value={`${cur.feels_like}°`} testId="weather-detail-feels" />
              <Stat icon={Droplets} label="Umidade" value={`${cur.humidity}%`} testId="weather-detail-humidity" />
              <Stat icon={Wind} label="Vento" value={`${cur.wind_kmh} km/h`} testId="weather-detail-wind" />
            </div>
          )}
        </div>

        {data && (
          <div className="px-5 py-5 space-y-6">
            <section>
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="font-display text-lg font-black uppercase text-[var(--fi-blue)]">Chance de chuva por hora</h3>
                <span className="text-[11px] text-gray-400 font-bold">Próximas 24h</span>
              </div>
              <div className="flex gap-2 overflow-x-auto snap-x pb-1 -mx-5 px-5" style={{ scrollbarWidth: "none" }} data-testid="weather-hourly-track">
                {data.hourly.map((h) => <HourCard key={h.time} h={h} />)}
              </div>
            </section>

            <section className="rounded-2xl bg-white p-4 shadow-[0_6px_20px_rgba(13,27,75,0.06)] border border-gray-100">
              <h3 className="font-display text-lg font-black uppercase text-[var(--fi-blue)] mb-1">Próximos 7 dias</h3>
              <div data-testid="weather-daily-list">
                {data.daily.map((d) => <DayRow key={d.date} d={d} min={allMin} max={allMax} />)}
              </div>
            </section>

            {today && (
              <div className="flex items-center justify-center gap-6 text-xs text-gray-500 font-bold pb-2">
                <span className="inline-flex items-center gap-1.5"><Sunrise className="w-4 h-4 text-amber-500" /> Nascer {today.sunrise}</span>
                <span className="inline-flex items-center gap-1.5"><Sunset className="w-4 h-4 text-[var(--fi-red)]" /> Pôr do sol {today.sunset}</span>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default WeatherDetailSheet;
